import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { detectTimezone } from './timezone-detector';

/**
 * HTTP Interceptor that adds a timezone header to requests targeting
 * the Admin API and Appointment Scheduling API.
 *
 * The interceptor detects the user's timezone using the browser's Intl API
 * and adds it as an X-Timezone header to all requests that match the
 * configured base URLs for adminApiBaseUrl and appointmentSchedulingBaseUrl.
 *
 * The timezone is detected once and cached for the lifetime of the interceptor
 * instance to improve performance.
 *
 * If timezone detection fails, requests proceed without the timezone header
 * to ensure graceful degradation.
 */
@Injectable()
export class TimezoneInterceptor implements HttpInterceptor {
  /**
   * Cached timezone value. Undefined means not yet detected, null means detection failed.
   */
  private cachedTimezone: string | null | undefined;

  constructor(private environmentService: EnvironmentService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Lazy-load timezone on first request
    if (this.cachedTimezone === undefined) {
      this.cachedTimezone = detectTimezone();
    }

    // Skip if timezone detection failed
    if (!this.cachedTimezone) {
      return next.handle(req);
    }

    // Check if request targets admin API or appointment scheduling API
    const adminApiBaseUrl = this.environmentService.adminApiBaseUrl;
    const appointmentSchedulingBaseUrl =
      this.environmentService.appointmentSchedulingBaseUrl;

    const shouldAddTimezone =
      (adminApiBaseUrl && req.url.startsWith(adminApiBaseUrl)) ||
      (appointmentSchedulingBaseUrl &&
        req.url.startsWith(appointmentSchedulingBaseUrl));

    // Add timezone header only for matching URLs
    if (shouldAddTimezone) {
      const clonedRequest = req.clone({
        setHeaders: {
          'X-Timezone': this.cachedTimezone,
        },
      });
      return next.handle(clonedRequest);
    }

    // Pass through unchanged for other URLs
    return next.handle(req);
  }
}
